/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Group,
  Loader,
  Stack,
  Text
} from "@mantine/core";
import { useIntegrationApp } from "@membranehq/react";
import { toast } from "sonner";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useEffect, useState, memo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { HubSpotField } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";
import { useCreateBulkFieldMappings, useUpdateFieldMapping } from "../../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { Alert as DsAlert, AlertDescription } from "../../../../../components/ui/alert";
import { Button as DsButton } from "../../../../../components/ui/button";
import { SearchableMenuSelect } from "../../../../../components/ui/searchable-menu-select";

interface RoleMapping {
  hyperengage_role: string;
  hubspot_owner_field: string;
  display_name: string;
}

interface RoleMappingTableProps {
  slug: string;
  roleMappings: RoleMapping[];
  onChange: (mappings: RoleMapping[]) => void;
  isConnected: boolean;
  hubspotFields: HubSpotField[];
  connectionId?: string;
  usedHubSpotFields?: Set<string>; // Fields already used in field mappings or matcher
  syncId?: string; // Required for creating field mappings
  workspaceFields?: any[]; // Required for finding role field IDs
  existingMappingIds?: Record<string, string>; // For updating existing role field mappings
  providerLabel?: string;
  providerKey?: string;
  hideHeader?: boolean;
  persistenceMode?: "component" | "managed";
  reservedProviderFields?: Set<string>;
}

const DEFAULT_ROLE_MAPPINGS: Record<string, RoleMapping[]> = {
  accounts: [
    { hyperengage_role: "csm_id", hubspot_owner_field: "", display_name: "Customer Success Manager" },
    { hyperengage_role: "owner_id", hubspot_owner_field: "", display_name: "Account Owner" },
    { hyperengage_role: "ae_id", hubspot_owner_field: "", display_name: "Account Executive" }
  ],
  companies: [
    { hyperengage_role: "csm_id", hubspot_owner_field: "", display_name: "Customer Success Manager" },
    { hyperengage_role: "owner_id", hubspot_owner_field: "", display_name: "Account Owner" },
    { hyperengage_role: "ae_id", hubspot_owner_field: "", display_name: "Account Executive" }
  ]
};

const RoleMappingTable = ({
  slug,
  roleMappings,
  onChange,
  isConnected,
  hubspotFields,
  connectionId,
  usedHubSpotFields = new Set(),
  syncId,
  workspaceFields = [],
  existingMappingIds = {},
  providerLabel = 'HubSpot',
  providerKey = 'hubspot',
  hideHeader = false,
  persistenceMode = "component",
  reservedProviderFields = new Set()
}: RoleMappingTableProps) => {
  const integrationApp = useIntegrationApp();
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const queryClient = useQueryClient();
  const rowInputClass = "h-9 w-full rounded-[5px] border border-border-chip bg-background px-3 text-[13px] text-foreground";

  // Mutation hooks
  const createBulkFieldMappingsMutation = useCreateBulkFieldMappings();
  const updateFieldMappingMutation = useUpdateFieldMapping();
  const [loading, setLoading] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  // Hydrate role mappings from integration.app importValue (if present)
  useEffect(() => {
    const loadFromIntegrationApp = async () => {
      if (!isConnected || !connectionId) return;
      if (slug !== 'accounts' && slug !== 'companies') return;
      try {
        setLoading(true);
        const fieldMappingKey = slug;
        const fieldMappingInstance = await integrationApp
          .connection(connectionId)
          .fieldMapping(fieldMappingKey)
          .get({ autoCreate: true });
        const importValue: Record<string, any> = fieldMappingInstance?.importValue || {};
        const imported: RoleMapping[] = [];

        Object.entries(importValue).forEach(([key, value]) => {
          // Role mappings are represented by $findAppRecordId with a $var recordId
          const v: any = value;
          const recordIdVar = v?.$findAppRecordId?.recordId?.$var;
          if (recordIdVar && typeof recordIdVar === 'string') {
            const hubspotField = recordIdVar.startsWith('$.') ? recordIdVar.slice(2) : recordIdVar;
            // Only consider known roles for this table
            if (['csm_id', 'owner_id', 'ae_id'].includes(key)) {
              // Keep existing display name if we can find it in workspace fields
              const display = (workspaceFields)?.find((f: any) => f?.name === key)?.display_name ||
                key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
              imported.push({ hyperengage_role: key, hubspot_owner_field: hubspotField, display_name: display });
            }
          }
        });

        if (imported.length) {
          // Merge with existing roleMappings, preferring explicit user edits
          const byRole = new Map(roleMappings.map(r => [r.hyperengage_role, r]));
          imported.forEach(r => {
            const existing = byRole.get(r.hyperengage_role);
            if (!existing || !existing.hubspot_owner_field) {
              byRole.set(r.hyperengage_role, r);
            }
          });
          const merged = Array.from(byRole.values());
          // Only call onChange if there is an actual change
          const changed = merged.length !== roleMappings.length || merged.some((m, i) => {
            const e = roleMappings[i];
            return !e || e.hyperengage_role !== m.hyperengage_role || e.hubspot_owner_field !== m.hubspot_owner_field;
          });
          if (changed) onChange(merged);
        }
      } catch (e) {
        // ignore; absence of importValue role mappings is valid
      } finally {
        setLoading(false);
      }
    };
    loadFromIntegrationApp().catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, connectionId, slug]);

  // Get the display mappings, ensuring all default roles are always shown
  const displayMappings: RoleMapping[] = (() => {
    if (slug !== 'accounts' && slug !== 'companies') {
      return roleMappings;
    }

    const defaultRoles = DEFAULT_ROLE_MAPPINGS[slug] || [];
    const mergedMappings: RoleMapping[] = [];

    // First, add all default roles (with existing values if available)
    defaultRoles.forEach(defaultRole => {
      const existingMapping = roleMappings.find(rm => rm.hyperengage_role === defaultRole.hyperengage_role);

      if (existingMapping) {
        // Use existing mapping (keeps the hubspot_owner_field value)
        mergedMappings.push(existingMapping);
      } else {
        // Use default mapping (empty hubspot_owner_field)
        mergedMappings.push(defaultRole);
      }
    });

    // Add any additional custom role mappings that don't match default roles
    roleMappings.forEach(roleMapping => {
      if (!defaultRoles.find(dr => dr.hyperengage_role === roleMapping.hyperengage_role)) {
        mergedMappings.push(roleMapping);
      }
    });

    return mergedMappings;
  })();

  // Filter HubSpot fields to only show owner/user fields
  const ownerFields = hubspotFields.filter(field =>
    field.key.toLowerCase().includes('owner') ||
    field.key.toLowerCase().includes('user') ||
    field.title?.toLowerCase().includes('owner') ||
    field.title?.toLowerCase().includes('user')
  );

  const handleRoleMappingChange = (index: number, hubspotField: string) => {
    // Work with displayMappings for UI, but save back to roleMappings
    const updatedMapping = { ...displayMappings[index], hubspot_owner_field: hubspotField };

    // Update the actual roleMappings array
    const existingIndex = roleMappings.findIndex(rm => rm.hyperengage_role === updatedMapping.hyperengage_role);

    const newMappings = [...roleMappings];
    if (existingIndex >= 0) {
      // Update existing mapping
      newMappings[existingIndex] = updatedMapping;
    } else {
      // Add new mapping
      newMappings.push(updatedMapping);
    }

    onChange(newMappings);
  };

  const handleAddRoleMapping = () => {
    const newMapping: RoleMapping = {
      hyperengage_role: "",
      hubspot_owner_field: "",
      display_name: "Custom Role"
    };
    onChange([...roleMappings, newMapping]);
  };

  const handleRemoveRoleMapping = (index: number) => {
    // Remove from displayMappings, but work with roleMappings
    const mappingToRemove = displayMappings[index];

    // Remove from actual roleMappings array
    const newMappings = roleMappings.filter(rm => rm.hyperengage_role !== mappingToRemove.hyperengage_role);
    onChange(newMappings);
  };

  // Save role mappings as field mappings to database with is_owner_assignment: true
  const saveRoleMappingsToDatabase = async () => {
    if (!workspace?.id || !syncId) return;

    const validRoleMappings = roleMappings.filter(rm => rm.hyperengage_role && rm.hubspot_owner_field);
    if (validRoleMappings.length === 0) return;

    // Helper function to convert UI slug to backend object name (not used here but kept for consistency)
    // Role mappings don't need object conversion as they work with field IDs directly

    // Prepare field mappings data for role mappings
    const updatePromises: Promise<any>[] = [];
    const newMappings: any[] = [];

    validRoleMappings.forEach(roleMapping => {
      // Find the workspace field for this role
      const roleField = workspaceFields.find((f: any) => f.name === roleMapping.hyperengage_role);

      if (roleField) {
        const isMetricField = roleField.attribute_type === 'metric_traits';
        const fieldMappingData: any = {
          source_field_name: roleMapping.hubspot_owner_field,
          source_field_id: roleMapping.hubspot_owner_field,
          destination_field_type: isMetricField ? "metric" : "field",
          is_owner_assignment: true,
          transform_rules: {},
          is_required: false
        };

        if (isMetricField) {
          // For metric fields, set destination_metric_name instead of destination_field_id
          fieldMappingData.destination_metric_name = roleField.name;
          fieldMappingData.destination_field_id = null;
          fieldMappingData.metric_aggregation_type = 'value';
        } else {
          // For regular fields, set destination_field_id
          fieldMappingData.destination_field_id = roleField.id;
          // Don't send empty string for destination_metric_name
        }

        // Check if this role mapping already exists as a field mapping
        const existingMappingId = existingMappingIds[roleMapping.hubspot_owner_field];

        if (existingMappingId) {
          // Update existing mapping
          updatePromises.push(
            updateFieldMappingMutation.mutateAsync({
              workspace_id: workspace.id,
              sync_id: syncId,
              mapping_id: existingMappingId,
              data: fieldMappingData
            })
          );
        } else {
          // Create new mapping
          newMappings.push(fieldMappingData);
        }
      }
    });

    // Execute all updates
    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);
    }

    // Create new mappings
    if (newMappings.length > 0) {
      await createBulkFieldMappingsMutation.mutateAsync({
        workspace_id: workspace.id,
        sync_id: syncId,
        mappings: newMappings as any
      });
    }

    // Invalidate field mappings query to refresh data
    await queryClient.invalidateQueries({ queryKey: ["field-mappings", workspace.id, syncId] });
  };

  const saveRoleMappingsToIntegrationApp = async () => {
    if (!connectionId || !isConnected) return;

    const fieldMappingKey = slug;

    // Get current field mapping
    const fieldMappingInstance = await integrationApp
      .connection(connectionId)
      .fieldMapping(fieldMappingKey)
      .get({
        autoCreate: true
      });

    const currentImportValue = fieldMappingInstance?.importValue || {};

    // Create role mapping entries using $findAppRecordId pattern
    const roleMappingEntries: Record<string, any> = {};

    roleMappings.forEach(mapping => {
      if (mapping.hyperengage_role && mapping.hubspot_owner_field) {
        roleMappingEntries[mapping.hyperengage_role] = {
          $findAppRecordId: {
            dataLinkTable: {
              key: "users"
            },
            recordId: {
              $var: `$.${mapping.hubspot_owner_field}`
            }
          }
        };
      }
    });

    // Preserve existing relationship mappings and field mappings while replacing role mappings
    const preservedMappings: Record<string, any> = {};
    // Preserve everything that is NOT a role mapping; i.e., entries without $findAppRecordId
    Object.entries(currentImportValue).forEach(([key, value]: [string, any]) => {
      // Preserve relationship mappings
      if (key === 'relationships' && (value?.$iterate || value?.$jsonata)) {
        preservedMappings[key] = value;
      } else if (value?.$var && !['csm_id', 'owner_id', 'ae_id'].includes(key)) {
        // Preserve field mappings (those with $var pattern that are not role mappings)
        preservedMappings[key] = value;
      }
    });

    // Combine preserved mappings with new role mappings for clean state
    const updatedImportValue = {
      ...preservedMappings,
      ...roleMappingEntries
    };

    // Update the field mapping using put for clean state
    const completeData: any = {
      ...fieldMappingInstance,
      importValue: updatedImportValue
    };

    await integrationApp
      .connection(connectionId)
      .fieldMapping(fieldMappingKey)
      .put(completeData);
  };

  // Save role mappings to both database (as field mappings) and integration.app in parallel
  const saveRoleMappings = async () => {
    if (!connectionId || !isConnected || !workspace?.id || !syncId) return;

    setSavingMapping(true);

    // Show loading notification
    toast.loading('Saving role mappings...', {
      id: 'saving-role-mappings',
      description: 'Updating role assignments in database and integration',
    });

    try {
      // Execute both save operations in parallel for better performance
      await Promise.all([
        saveRoleMappingsToDatabase(),
        saveRoleMappingsToIntegrationApp()
      ]);

      // Show success notification
      toast.success('Role mappings saved!', {
        id: 'saving-role-mappings',
        description: 'Role assignments have been successfully updated',
      });
    } catch (error) {
      console.error('Failed to save role mappings:', error);

      // Show error notification
      toast.error('Failed to save role mappings', {
        id: 'saving-role-mappings',
        description: 'An error occurred while saving role assignments. Please try again.',
      });
    } finally {
      setSavingMapping(false);
    }
  };

  if (!isConnected) {
    return (
      <DsAlert className="border-orange-300 bg-orange-50 px-4 py-3">
        <AlertDescription className="text-xs text-orange-900">
          Connect to {providerLabel} first to configure role mappings
        </AlertDescription>
      </DsAlert>
    );
  }

  // Only show for accounts and companies
  if (slug === 'contacts') {
    return null;
  }

  return (
    <Stack gap="sm">
      {!hideHeader && (
        <>
          <Group justify="space-between">
            <Text fw={500} size="sm">Map team member roles</Text>
          </Group>

          <Text size="xs" c="dimmed">Connect your team roles to corresponding {providerLabel} owner ID fields. This ensures proper account ownership across both systems.</Text>
        </>
      )}

      {loading ? (
        <Group justify="center" p="md">
          <Loader size="sm" />
          <Text size="xs">Loading role mappings...</Text>
        </Group>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3">
            <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">Hyperengage Role</Text>
            <Text size="xs" fw={600} c="dimmed" className="uppercase tracking-wide">{providerLabel} Field</Text>
            <span />
          </div>

          {displayMappings.map((mapping, index) => {
            const isDefaultRole = !!DEFAULT_ROLE_MAPPINGS[slug]?.some(dm => dm.hyperengage_role === mapping.hyperengage_role);
            const ownerFieldOptions = ownerFields
              .map((field) => ({
              value: field.key,
              label: `${field.title} (${field.type})`,
              searchText: `${field.key} ${field.type || ""}`,
            }))
              .filter((field) => {
                const isCurrentSelection = field.value === mapping.hubspot_owner_field;
                if (isCurrentSelection) return true;
                if (usedHubSpotFields.has(field.value)) return false;
                if (reservedProviderFields.has(field.value)) return false;
                return true;
              });

            return (
              <div
                key={`role-mapping-${mapping.hyperengage_role}-${mapping.hubspot_owner_field || 'new'}`}
                className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_32px] items-center gap-3"
              >
                {isDefaultRole ? (
                  <div className="h-10 rounded-[8px] border border-[var(--mantine-color-default-border)] bg-[var(--mantine-color-gray-0)] px-3 flex flex-col justify-center overflow-hidden">
                    <Text size="sm" fw={500} truncate>{mapping.display_name || mapping.hyperengage_role || "Custom Role"}</Text>
                    <Text size="xs" c="dimmed" truncate>{mapping.hyperengage_role || "custom_role"}</Text>
                  </div>
                ) : (
                  <input
                    type="text"
                    value={mapping.hyperengage_role}
                    placeholder="custom_role_key"
                    onChange={(event) => {
                      const roleValue = event.currentTarget.value;
                      const roleIndex = roleMappings.findIndex((rm) => (
                        rm.hyperengage_role === mapping.hyperengage_role
                        && rm.hubspot_owner_field === mapping.hubspot_owner_field
                      ));
                      if (roleIndex < 0) return;
                      const nextMappings = [...roleMappings];
                      nextMappings[roleIndex] = {
                        ...nextMappings[roleIndex],
                        hyperengage_role: roleValue,
                        display_name: roleValue
                          ? roleValue.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                          : "Custom Role",
                      };
                      onChange(nextMappings);
                    }}
                    className="h-10 w-full rounded-[8px] border border-[var(--mantine-color-default-border)] bg-white px-3 text-[13px] text-[var(--mantine-color-text)]"
                  />
                )}

                <SearchableMenuSelect
                  value={mapping.hubspot_owner_field || ""}
                  options={[
                    { value: "", label: `Select ${providerLabel} owner field...`, searchText: "clear unselect" },
                    ...ownerFieldOptions,
                  ]}
                  placeholder={`Select ${providerLabel} owner field...`}
                  onChange={(value) => {
                    handleRoleMappingChange(index, value);
                  }}
                  className={rowInputClass}
                />

                <button
                  type="button"
                  onClick={() => handleRemoveRoleMapping(index)}
                  disabled={isDefaultRole}
                  className="h-9 w-8 rounded flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-status-cancelled transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Remove role mapping row"
                >
                  <IconTrash size="0.75rem" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="pt-2">
        <DsButton
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-[12px]"
          onClick={handleAddRoleMapping}
        >
          <IconPlus size="0.75rem" className="mr-1.5" />
          Add a trait
        </DsButton>
      </div>

      {persistenceMode === "component" && roleMappings.some(m => m.hubspot_owner_field) && (
        <Group justify="flex-end">
          <DsButton
            type="button"
            size="sm"
            className="h-8 text-[12px]"
            onClick={() => {
              saveRoleMappings().catch(console.error);
            }}
            disabled={!roleMappings.some((mapping) => mapping.hubspot_owner_field) || savingMapping}
          >
            {savingMapping ? "Saving..." : "Save Mappings"}
          </DsButton>
        </Group>
      )}
    </Stack>
  );
};

export default memo(RoleMappingTable); 
